import type { BlotterOrderEntry } from '@/store/blotter/types/BlotterOrderEntry.ts';
import { getOMSCurrencyFromBlotterCurrency } from '@/store/orders/helpers.ts';
import type { OrderStateCurrencyPair } from '@/store/orders/types.ts';
import type { Order, OrderMode } from '@/store/types.ts';
import type { OmsErrors, OmsWarning } from '@/types/common.ts';
import type { Currency } from '@/types/currency.ts';
import type { ISODate, StringTime } from '@/types/date.ts';

export type CallOrder = Order<'CallOrder', CallOrderProperties>;

export type CallOrderProperties = {
  status:
    | { type: 'creating' }
    | { type: 'pending' }
    | { type: 'failed'; errorMessage: string }
    | { type: 'submitted' | 'editing' | 'cancelled'; tradeId: string };

  currencyPair?: OrderStateCurrencyPair;

  limitPrice?: number;
  expiry: GoodTillCancelled | ExpiryDate;

  notificationEmails?: string[];

  isReadyToSubmit: boolean;
  clockOffset?: number; // offset in milliseconds between the backend and the client

  // to detect if the order is in EDIT or VIEW mode
  // EDIT mode is used when the user is editing an existing order
  // VIEW mode is used when the user is viewing an existing order
  mode: OrderMode;

  errors?: OmsErrors;
  warnings?: OmsWarning;
};

export type StopLossOrderError = {
  field?: CallOrderFieldName;
  message: string;
  code: number;
};

export enum CallOrderFieldName {
  id = 'id',
  currencyPair = 'currencyPair',
  bdrId = 'bdrId',
  currency = 'currency',
  limitPrice = 'limitPrice',
  expiryDate = 'expiryDate',
  notificationEmails = 'notificationEmails',
}

export type GoodTillCancelled = {
  type: 'GoodTillCancelled';
};

export type ExpiryDate = {
  type: 'ExpiryDate';
  date?: ISODate;
  time?: StringTime;
};

export const generateCallOrder = (): CallOrder => ({
  type: 'CallOrder',
  properties: {
    status: { type: 'creating' },
    currencyPair: undefined,
    limitPrice: undefined,
    expiry: { type: 'GoodTillCancelled' },
    notificationEmails: undefined,
    errors: {},
    isReadyToSubmit: false,
    clockOffset: 0,
    mode: 'VIEW',
  },
});

export const generateCallOrderFromBlotterEntry = (entry: BlotterOrderEntry, mode: OrderMode): CallOrder => {
  return {
    id: entry.id,
    type: 'CallOrder',
    clientId: undefined,

    properties: {
      mode,
      status: { type: 'submitted', tradeId: entry.id },
      currencyPair: getOMSCurrencyFromBlotterCurrency(entry.currencyPair),
      limitPrice: entry.limitPrice,
      expiry: entry.isGtc
        ? { type: 'GoodTillCancelled' }
        : { type: 'ExpiryDate', date: entry.expiryDay ?? undefined, time: entry.expiryTime ?? undefined },
      notificationEmails: undefined,
      isReadyToSubmit: false,
      clockOffset: 0,
    },
  };
};

export type ValidateCallOrderRequest = {
  tileId: string;
  clientId: number;
  currency1: Currency;
  currency2: Currency;

  limitPrice?: number;
  expiry: GoodTillCancelled | ExpiryDate;
  notificationEmails?: {
    isOverridenEmails?: boolean;
    emails: string[];
  };
  clockOffset?: number; // offset in milliseconds between the backend and the client
};

export type SubmitCallOrderRequest = {
  tileId: string;
  clientId: number;
  currency1: Currency;
  currency2: Currency;
  limitPrice: number;
  expiry: GoodTillCancelled | ExpiryDate;
  notificationEmails?: string[];
  clockOffset?: number; // offset in milliseconds between the backend and the client
};

export const mapCallOrder = (
  product: {
    type: 'CallOrder';
    clientId?: number;
    properties: CallOrderProperties;
    tileId: string;
  },
  defaultClientId: number,
): ValidateCallOrderRequest => {
  return {
    tileId: product.tileId,
    currency1: product.properties.currencyPair?.currency1 ?? '',
    currency2: product.properties.currencyPair?.currency2 ?? '',
    clientId: product.clientId ?? defaultClientId,
    limitPrice: product.properties.limitPrice,
    expiry: product.properties.expiry,
    notificationEmails: {
      isOverridenEmails: false,
      emails: product.properties.notificationEmails ?? [],
    },
    clockOffset: 0,
  };
};
